/**
 * Hybrid designation picker for the Kronos tracker.
 *
 * The picker fuses two sources:
 *   1. Designations already attached to the project (subcollection
 *      `projects/{id}/designations`) — including custom ones with no
 *      catalog link.
 *   2. The 90 standard catalog entries (collection `designationCatalog`),
 *      with each entry's variants exploded into one option per variant.
 *
 * Output options carry an **encoded id** so the parent can route a
 * submit to either:
 *   - "stamp this existing designation directly on the TimeEntry", or
 *   - "auto-attach this catalog entry to the project, then stamp".
 *
 * Pure module — no React, no Firestore. Unit-tested.
 */

import type {
  CatalogDesignation,
  Designation,
  DesignationSection,
  TimeEntry,
} from '@altyor-kronos/shared-types';
import { dedupeDesignations } from './dedupeDesignations';
import { compareDesignationNames } from '../projects/designations';

// ─── Encoded option ids ─────────────────────────────────────────────

export type DecodedOptionId =
  | { kind: 'existing'; id: string }
  | { kind: 'catalog'; catalogId: string; variant: string | null }
  | { kind: 'custom' };

export const CUSTOM_OPTION_ID = 'custom:new';

export function encodeExistingOptionId(designationId: string): string {
  return `existing:${designationId}`;
}

/**
 * The variant is appended after `#`. If a variant ever contains a `#`,
 * `decodeDesignationOptionId` recovers it by splitting only on the
 * first occurrence — round-trip safe.
 */
export function encodeCatalogOptionId(catalogId: string, variant: string | null): string {
  return variant === null ? `catalog:${catalogId}` : `catalog:${catalogId}#${variant}`;
}

export function decodeDesignationOptionId(encoded: string): DecodedOptionId | null {
  if (!encoded) return null;
  if (encoded === CUSTOM_OPTION_ID) return { kind: 'custom' };
  if (encoded.startsWith('existing:')) {
    const id = encoded.slice('existing:'.length);
    if (!id) return null;
    return { kind: 'existing', id };
  }
  if (encoded.startsWith('catalog:')) {
    const rest = encoded.slice('catalog:'.length);
    if (!rest) return null;
    const hashIdx = rest.indexOf('#');
    if (hashIdx === -1) {
      return { kind: 'catalog', catalogId: rest, variant: null };
    }
    return {
      kind: 'catalog',
      catalogId: rest.slice(0, hashIdx),
      variant: rest.slice(hashIdx + 1),
    };
  }
  return null;
}

// ─── Existing-designation matcher ───────────────────────────────────

/**
 * Find an existing project designation whose `(catalogId, variant)`
 * tuple matches the candidate. Custom designations (no `catalogId`)
 * are skipped — they cannot be matched from the catalog side.
 *
 * Used by the auto-attach path: before creating a new designation on
 * the project, the parent first looks for an existing one to reuse.
 */
export function matchExistingDesignation(
  existing: readonly Designation[],
  catalogId: string,
  variant: string | null,
): Designation | undefined {
  return existing.find((d) => {
    if (!d.catalogId) return false;
    if (d.catalogId !== catalogId) return false;
    const dv = d.variant ?? null;
    return dv === variant;
  });
}

// ─── Picker option model ────────────────────────────────────────────

export type DesignationOptionKind = 'existing' | 'catalog' | 'custom';

export interface DesignationOption {
  /** Encoded id (see encode/decode helpers). Stable across renders. */
  id: string;
  kind: DesignationOptionKind;
  label: string;
  section: DesignationSection;
  groupLabel: string;
  /**
   * True when this is an existing project designation the current user
   * has logged time against recently (drives the "récent" badge in the
   * form picker). Always false for catalog/custom options.
   */
  recent: boolean;
  /**
   * Owning project. Only set on cross-project options (see
   * `buildRecentPairOptions`) — picking one must also select its project.
   */
  projectId?: string;
  /** Catalog-only fields. */
  catalogId?: string;
  variant?: string | null;
}

export interface BuildOptionsConfig {
  sectionLabel: (section: DesignationSection) => string;
  existingGroupLabel?: string;
  /** Label for the "create custom" option shown at the top. */
  customOptionLabel?: string;
  /**
   * When set, recently-used project designations move into their own
   * group under this label, leaving the *complete* project list visible
   * below it — a non-recent designation stays one scroll away instead of
   * being buried under the recent ones.
   */
  recentGroupLabel?: string;
}

const SECTION_ORDER: readonly DesignationSection[] = [
  'rd',
  'npi',
  'prototypes',
  'nre',
  'certification',
  'production',
  'autres',
];

function variantLabel(name: string, variant: string): string {
  return `${name} — ${variant}`;
}

/**
 * Build the picker options. Existing project designations come first
 * (under a single "On this project" group), then catalog options
 * grouped by section in canonical order. Catalog options whose
 * `(catalogId, variant)` already lives on the project are filtered out
 * to avoid duplicate rows.
 */
export function buildDesignationOptions(
  existing: readonly Designation[],
  catalog: readonly CatalogDesignation[],
  config: BuildOptionsConfig,
  recentExistingIds?: ReadonlyMap<string, number>,
): DesignationOption[] {
  const existingGroup = config.existingGroupLabel ?? 'On this project';
  const taken = new Set<string>();
  const existingOptions: DesignationOption[] = [];

  // Archived designations drop out here, before the `taken` dedup — so
  // archiving a project line hands the pick back to its catalog twin
  // instead of leaving a hole. Single point: all four call sites (tracker,
  // calendar, timesheet) inherit the filter.
  //
  // Defensive dedup: dev seeds historically created duplicate designation
  // docs. Collapse them before building options (also removes the React
  // duplicate-key warning). See dedupeDesignations.ts.
  const dedupedExisting = dedupeDesignations(existing.filter((d) => !d.isArchived));

  // Order: recently-used designations first (most recent first, keyed by
  // raw doc id), then the rest alphabetically by label. Dedup happens
  // first, so a dropped duplicate's id in the recency map can't resurrect
  // it. With no recency map, everything falls into the alphabetical bucket.
  const sortedExisting = [...dedupedExisting].sort((a, b) => {
    const ra = recentExistingIds?.get(a.id);
    const rb = recentExistingIds?.get(b.id);
    if (ra !== undefined && rb !== undefined) return rb - ra;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return compareDesignationNames(a.name, b.name);
  });

  for (const d of sortedExisting) {
    const recent = recentExistingIds?.has(d.id) ?? false;
    existingOptions.push({
      id: encodeExistingOptionId(d.id),
      kind: 'existing',
      label: d.name,
      section: d.section,
      // Recents are sorted first, so the two groups stay contiguous —
      // MUI's groupBy repeats a header for every run of a group.
      groupLabel: recent && config.recentGroupLabel ? config.recentGroupLabel : existingGroup,
      recent,
    });
    if (d.catalogId) {
      taken.add(`${d.catalogId}::${d.variant ?? ''}`);
    }
  }

  const catalogOptions: DesignationOption[] = [];
  // Sort catalog by canonical section order, then alphabetically by name
  // to keep the picker stable across snapshots.
  const sortedCatalog = [...catalog].sort((a, b) => {
    const sa = SECTION_ORDER.indexOf(a.section as DesignationSection);
    const sb = SECTION_ORDER.indexOf(b.section as DesignationSection);
    if (sa !== sb) return sa - sb;
    return compareDesignationNames(a.name, b.name);
  });

  for (const c of sortedCatalog) {
    const section = c.section as DesignationSection;
    const groupLabel = config.sectionLabel(section);
    if (c.variants && c.variants.length > 0) {
      for (const v of c.variants) {
        if (taken.has(`${c.id}::${v}`)) continue;
        catalogOptions.push({
          id: encodeCatalogOptionId(c.id, v),
          kind: 'catalog',
          label: variantLabel(c.name, v),
          section,
          groupLabel,
          recent: false,
          catalogId: c.id,
          variant: v,
        });
      }
    } else {
      if (taken.has(`${c.id}::`)) continue;
      catalogOptions.push({
        id: encodeCatalogOptionId(c.id, null),
        kind: 'catalog',
        label: c.name,
        section,
        groupLabel,
        recent: false,
        catalogId: c.id,
        variant: null,
      });
    }
  }

  const result: DesignationOption[] = [];

  // "Create custom" option always first so it's easy to find.
  if (config.customOptionLabel) {
    result.push({
      id: CUSTOM_OPTION_ID,
      kind: 'custom',
      label: config.customOptionLabel,
      section: 'autres',
      groupLabel: config.sectionLabel('autres'),
      recent: false,
    });
  }

  result.push(...existingOptions, ...catalogOptions);
  return result;
}

// ─── Cross-project "recently used" options ──────────────────────────

/**
 * Options for a designation picker that has **no project selected yet**
 * (the calendar popover's empty state). One option per
 * `(project, designation)` pair the user logged recently, most recent
 * first, labelled `CODE — Designation`. Each option carries its
 * `projectId` so picking one fills the project field too.
 *
 * `entries` must come most-recent-first (the API's natural order) — the
 * first occurrence of a pair wins. Pairs whose project code or
 * designation label is unknown are skipped: they could neither be
 * rendered nor pre-filled.
 */
export function buildRecentPairOptions(
  entries: readonly TimeEntry[],
  projectCodes: ReadonlyMap<string, string>,
  designationLabels: ReadonlyMap<string, string>,
  groupLabel: string,
  limit = 8,
): DesignationOption[] {
  const out: DesignationOption[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    if (out.length >= limit) break;
    const key = `${e.projectId}::${e.designationId}`;
    if (seen.has(key)) continue;
    const code = projectCodes.get(e.projectId);
    const name = designationLabels.get(e.designationId);
    if (!code || !name) continue;
    seen.add(key);
    out.push({
      id: encodeExistingOptionId(e.designationId),
      kind: 'existing',
      label: `${code} — ${name}`,
      section: 'autres',
      groupLabel,
      recent: true,
      projectId: e.projectId,
    });
  }
  return out;
}


// ─── Recency map ────────────────────────────────────────────────────

/**
 * Raw `designationId` → last used (ms), over one project, from the
 * current user's own entries. Feeds `buildDesignationOptions`'
 * `recentExistingIds`. Empty map when no project is selected.
 */
export function buildRecencyMap(
  entries: readonly TimeEntry[],
  projectId: string,
): Map<string, number> {
  const m = new Map<string, number>();
  if (!projectId) return m;
  for (const e of entries) {
    if (e.projectId !== projectId) continue;
    const ms = e.startTime.toMillis();
    const prev = m.get(e.designationId);
    if (prev === undefined || ms > prev) m.set(e.designationId, ms);
  }
  return m;
}
